import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { X, Download, Smartphone, Share } from "lucide-react";

export const InstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if dismissed recently
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return; // Don't show for 7 days after dismissal
      }
    }

    // Listen for beforeinstallprompt event (Chrome, Edge)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Show iOS prompt after a delay
    if (iOS && !standalone) {
      setTimeout(() => setShowPrompt(true), 3000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    setShowPrompt(false);
  };

  // Don't show if already installed or no prompt
  if (isStandalone || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 animate-fade-in" data-testid="install-prompt">
      <Card className="bg-[#f5f9f5] border-[#c5d5c5] shadow-xl">
        <CardContent className="p-4">
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 text-foreground/40 hover:text-foreground"
            aria-label="Dismiss"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#d4e4d4] flex items-center justify-center shrink-0">
              <Smartphone className="w-6 h-6 text-[#5a8a5a]" />
            </div>
            
            <div className="flex-1 pr-6">
              <h3 className="text-foreground font-semibold mb-1">Install Compass</h3>
              
              {isIOS ? (
                <div className="text-sm text-foreground/60">
                  <p className="mb-2">Add to your home screen for the best experience:</p>
                  <ol className="space-y-1 text-xs">
                    <li className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-[#e0ebe0] flex items-center justify-center text-xs font-medium">1</span>
                      Tap <Share className="w-4 h-4 inline mx-1" /> Share
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-[#e0ebe0] flex items-center justify-center text-xs font-medium">2</span>
                      Scroll down, tap "Add to Home Screen"
                    </li>
                  </ol>
                </div>
              ) : (
                <>
                  <p className="text-sm text-foreground/60 mb-3">
                    Get quick access and daily reminders on your phone.
                  </p>
                  <Button
                    onClick={handleInstall}
                    className="btn-primary text-sm py-2 px-4"
                    data-testid="install-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Install App
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InstallPrompt;
